import React, { useEffect, useRef, useState } from 'react';
import {
	Box,
	TextField,
	InputAdornment,
	IconButton,
	Stack,
	Button,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface MaterialGridToolbarProps {
	filter: string;
	setFilter: React.Dispatch<React.SetStateAction<string>>;
	onAdd?: () => void;
}

const MaterialGridToolbar: React.FC<MaterialGridToolbarProps> = ({
	filter,
	setFilter,
	onAdd,
}) => {
	const [localSearchInput, setLocalSearchInput] = useState(filter);
	const debounceTimer = useRef<NodeJS.Timeout>();

	useEffect(() => {
		if (filter === '' && localSearchInput !== '') {
			setLocalSearchInput('');
		}
	}, [filter]);

	useEffect(() => {
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}

		debounceTimer.current = setTimeout(() => {
			if (localSearchInput !== filter) {
				setFilter(localSearchInput);
			}
		}, 300);

		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
		};
	}, [localSearchInput, setFilter]);

	const handleClear = () => {
		setLocalSearchInput('');
		setFilter('');
	};

	return (
		<Box sx={{ paddingTop: 1, paddingBottom: 2 }}>
			<Stack direction="row" spacing={2} alignItems="center">
				<TextField
					fullWidth
					aria-label="Search"
					placeholder="Search"
					size="small"
					variant="outlined"
					value={localSearchInput}
					onChange={(e) => setLocalSearchInput(e.target.value)}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
							endAdornment: localSearchInput ? (
								<InputAdornment position="end">
									<IconButton
										aria-label="Clear search"
										onClick={handleClear}
										edge="end"
										size="small"
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : null,
						},
					}}
				/>

				{onAdd && (
					<Button
						variant="contained"
						color="primary"
						onClick={onAdd}
						sx={{
							minWidth: {
								xs: '90px',
								sm: '120px',
								md: '140px',
							},
						}}
					>
						Add Material
					</Button>
				)}
			</Stack>
		</Box>
	);
};

export default MaterialGridToolbar;
